/**
 * @file 学员绑定 / 修改教师弹窗
 * @description Select 选择一位教师；可按学员全部科目，或按单条科目详情写入。
 *
 * @whenToUse 学员列表「绑定老师」、所属教师列行内修改。
 * @whenNotToUse 编辑表单内手填教师；校区教师名单查看。
 */

import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import { FormField } from '@/components/ui/form-field.tsx'
import { Select } from '@/components/ui/select.tsx'
import {
  TEACHER_OPTIONS,
  type StudentRow,
} from '@/components/pages/student-page/mock.ts'

export type StudentBindTeacherDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  student: StudentRow | null
  /**
   * 科目详情 ID；传入时仅修改该科目教师。
   * 不传则同步到该学员全部科目。
   */
  detailId?: string
  /** 确认；teacherName 为选中教师姓名 */
  onConfirm: (teacherName: string) => void
}

/**
 * 绑定 / 修改教师 Dialog
 */
export function StudentBindTeacherDialog({
  open,
  onOpenChange,
  student,
  detailId,
  onConfirm,
}: StudentBindTeacherDialogProps) {
  const [teacherName, setTeacherName] = useState('')
  const [error, setError] = useState('')
  const isDetail = Boolean(detailId)

  useEffect(() => {
    if (!open || !student) return
    if (detailId) {
      const detail = student.details.find((item) => item.id === detailId)
      setTeacherName(detail?.teacherName ?? '')
    } else {
      setTeacherName(
        student.details.find((item) => item.teacherName)?.teacherName ?? '',
      )
    }
    setError('')
  }, [open, student, detailId])

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={isDetail ? '修改老师' : '绑定老师'}
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            取消
          </Button>
          <Button
            onClick={() => {
              if (!teacherName) {
                setError('请选择老师')
                return
              }
              onConfirm(teacherName)
              onOpenChange(false)
            }}
          >
            {isDetail ? '确认修改' : '确认绑定'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-muted">
          {isDetail
            ? `为「${student?.name}」当前科目选择所属老师。`
            : `为「${student?.name}」选择所属老师，将同步到该学员全部科目。`}
        </p>
        <FormField label="老师" required error={error}>
          <Select
            value={teacherName}
            options={TEACHER_OPTIONS}
            placeholder="请选择老师"
            invalid={Boolean(error)}
            onChange={(value) => {
              setTeacherName(value)
              setError('')
            }}
          />
        </FormField>
      </div>
    </Dialog>
  )
}
